import React, { useEffect, useMemo, useRef } from 'react';
import {
  App as AntdApp,
  Alert, Button, Card, Col, Divider, Form, InputNumber, Modal, Row,
  Select, Space, Spin, Switch, TimePicker, Typography, theme,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
import {
  AlertOutlined,
  AppstoreOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CreditCardOutlined,
  EyeInvisibleOutlined,
  FieldTimeOutlined,
  LayoutOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  SaveOutlined,
  SettingOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  clearHotelProfileProcess,
  requestHotelChannelList,
  requestHotelPaymentCodeList,
  requestHotelProfile,
  requestHotelSettingUpdate,
} from '@/AppRedux/actions/hotelProfile';
import { GalleryMode, HotelSettings } from '@/AppRedux/actions/hotelProfile/hotelType';
import { defaultHotelSettings } from '@/AppRedux/reducers/hotelProfile';
import { themeOptions } from '@/AppRedux/reducers/theme';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';
import { useParams } from 'react-router-dom';

const { Title, Text } = Typography;

const GALLERY_OPTIONS: { value: GalleryMode; icon: React.ReactNode; label: string; desc: string }[] = [
  { value: 'slide', icon: <PlayCircleOutlined />, label: 'Slide Gallery', desc: 'Full-width auto-playing carousel' },
  { value: 'grid',  icon: <AppstoreOutlined />,   label: 'Photo Grid',    desc: 'Masonry grid with gallery viewer' },
  { value: 'both',  icon: <LayoutOutlined />,      label: 'Both',          desc: 'Carousel followed by photo grid' },
  { value: 'none',  icon: <EyeInvisibleOutlined />, label: 'Hidden',       desc: 'No gallery shown on hotel page' },
];

function GalleryModePicker({ value, onChange }: { value?: GalleryMode | null; onChange?: (v: GalleryMode) => void }) {
  const { token } = theme.useToken();
  return (
    <Row gutter={[10, 10]}>
      {GALLERY_OPTIONS.map((opt) => {
        const selected = value === opt.value;
        return (
          <Col xs={12} key={opt.value}>
            <div
              onClick={() => onChange?.(opt.value)}
              style={{
                position: 'relative',
                border: `2px solid ${selected ? token.colorPrimary : token.colorBorderSecondary}`,
                background: selected ? token.colorPrimaryBg : token.colorBgContainer,
                borderRadius: token.borderRadiusLG,
                padding: '14px 12px',
                cursor: 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 22, color: selected ? token.colorPrimary : token.colorTextSecondary, display: 'block' }}>
                  {opt.icon}
                </span>
                <Text strong style={{ color: selected ? token.colorPrimary : token.colorText, fontSize: 13 }}>
                  {opt.label}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: '1.4' }}>
                  {opt.desc}
                </Text>
              </div>
              {selected && (
                <CheckCircleFilled
                  style={{ position: 'absolute', top: 10, right: 10, color: token.colorPrimary, fontSize: 16 }}
                />
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

const ConfigPage: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { token } = theme.useToken();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { propcode } = useParams<{ propcode?: string }>();

  const hotelProfileState = useAppSelector((state) => state.hotelProfile);

  const loading = hotelProfileState?.loading ?? false;
  const process = hotelProfileState?.process;
  const hotelSettings = hotelProfileState?.hotelSettings ?? defaultHotelSettings;
  const paymentCodeLoading = hotelProfileState?.paymentCodeLoading ?? false;
  const paymentCodeOptions = Array.isArray(hotelProfileState?.paymentCodeOptions)
    ? hotelProfileState.paymentCodeOptions
    : [];
  const channelLoading = hotelProfileState?.channelLoading ?? false;
  const channelOptions = Array.isArray(hotelProfileState?.channelOptions)
    ? hotelProfileState.channelOptions
    : [];

  const formInitialValues = useMemo(
    () => ({
      ...hotelSettings,
      gallery_mode: hotelSettings.gallery_mode ?? 'slide',
      booking_cutoff_time: hotelSettings.booking_cutoff_time
        ? dayjs(hotelSettings.booking_cutoff_time, 'HH:mm')
        : null,
    }),
    [hotelSettings],
  );

  const useCurrentFirstRender = useRef(true);

  useEffect(() => {
    if (!propcode) return;
    if (useCurrentFirstRender.current) {
      dispatch(requestHotelProfile(propcode));
      dispatch(requestHotelPaymentCodeList(propcode));
      dispatch(requestHotelChannelList(propcode));
    }
    return () => {
      useCurrentFirstRender.current = false;
    }
  }, [dispatch, propcode]);

  useEffect(() => {
    if (hotelSettings) {
      form.setFieldsValue({
        ...hotelSettings,
        gallery_mode: hotelSettings.gallery_mode ?? 'slide',
        booking_cutoff_time: hotelSettings.booking_cutoff_time
          ? dayjs(hotelSettings.booking_cutoff_time, 'HH:mm')
          : null,
      });
    }
  }, [form, hotelSettings]);

  useEffect(() => {
    if (process?.action !== 'HOTEL_SETTING_UPDATE') return;

    if (process.status) {
      message.success(process.message);
      dispatch(clearHotelProfileProcess());
      return;
    }

    Modal.error({
      title: 'Error',
      content: process.message,
      onOk: () => dispatch(clearHotelProfileProcess()),
    });
  }, [dispatch, message, process]);

  const onFinish = (values: Partial<HotelSettings>) => {
    const dsgValues = values.default_search_guests;
    const cutoffTimeValue = values.booking_cutoff_time as unknown;
    const booking_cutoff_time = dayjs.isDayjs(cutoffTimeValue)
      ? (cutoffTimeValue as dayjs.Dayjs).format('HH:mm')
      : (cutoffTimeValue as string | null | undefined) ?? null;

    dispatch(
      requestHotelSettingUpdate({
        ...values,
        default_search_guests: {
          adult: dsgValues?.adult ?? null,
          child: dsgValues?.child ?? null,
          infant: dsgValues?.infant ?? null,
        },
        booking_cutoff_time,
        propCode: propcode || null,
      }),
    );
  };

  const onReset = () => {
    const dsg = hotelSettings?.default_search_guests;
    form.setFieldsValue({
      ...hotelSettings,
      gallery_mode: hotelSettings?.gallery_mode ?? 'slide',
      default_search_guests: {
        adult: dsg?.adult ?? undefined,
        child: dsg?.child ?? undefined,
        infant: dsg?.infant ?? undefined,
      },
      booking_cutoff_time: hotelSettings?.booking_cutoff_time
        ? dayjs(hotelSettings.booking_cutoff_time, 'HH:mm')
        : null,
    });
  };

  const currentThemeCode = hotelSettings?.theme_code || 'default';
  const currentThemeName =
    themeOptions.find((item) => item.value === currentThemeCode)?.label ||
    themeOptions[0].label;

  const confirmPaymentLink = Form.useWatch('create_payment_link', form) as boolean | undefined;

  const cutoffEnabled = Form.useWatch('booking_cutoff_enabled', form) as boolean | undefined;

  // Watch the current deposit payment code in the form so the UI can keep
  // showing that value even if the refreshed option list does not include it.
  const selectedDepositPaymentCode = Form.useWatch(
    'deposit_payment_code',
    form,
  ) as HotelSettings['deposit_payment_code'];

  // Convert API/store items into Ant Design Select options.
  const depositPaymentOptions = paymentCodeOptions.map((option) => ({
    label: `${option.name} (${option.code})`,
    value: option.code,
  }));

  // Preserve the current saved value in the dropdown even if the latest API
  // response does not include that payment code yet.
  if (
    selectedDepositPaymentCode && !depositPaymentOptions.some((option) => option.value === selectedDepositPaymentCode)
  ) {
    depositPaymentOptions.unshift({
      label: selectedDepositPaymentCode,
      value: selectedDepositPaymentCode,
    });
  }

  const SectionHeader = ({
    title,
    icon,
    description,
  }: {
    title: string;
    icon: React.ReactNode;
    description?: string;
  }) => (
    <div style={{ marginBottom: 24 }}>
      <Space align="center" style={{ marginBottom: 8 }}>
        <div
          style={{
            backgroundColor: token.colorPrimaryBg,
            padding: 8,
            borderRadius: '50%',
            color: token.colorPrimary,
            display: 'flex',
          }}
        >
          {icon}
        </div>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
      </Space>
      {description ? (
        <Text type="secondary" style={{ display: 'block', marginLeft: 42 }}>
          {description}
        </Text>
      ) : null}
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <SettingOutlined /> System Configuration
        </Title>
        <Text type="secondary">
          Manage your hotel&apos;s global preferences and operational rules.
        </Text>
      </div>

      {hotelSettings ? (
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={formInitialValues}
            size="large"
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Card
                  variant="borderless"
                  style={{
                    height: '100%',
                    boxShadow: token.boxShadowTertiary,
                    borderRadius: token.borderRadiusLG,
                  }}
                >
                  <SectionHeader
                    title="Booking Rules"
                    icon={<FieldTimeOutlined />}
                    description="Manage booking constraints and payments."
                  />

                  <Row gutter={16}>
                    <Col span={24}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Space orientation="vertical" size={0}>
                          <Space>
                            <CreditCardOutlined />
                            <Text strong>Beam Online Payment</Text>
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Guests will be redirected to a payment page after booking.
                          </Text>
                        </Space>
                        <Form.Item name="create_payment_link" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                        </Form.Item>
                      </div>
                      <Alert
                        type={confirmPaymentLink ? 'success' : 'info'}
                        showIcon
                        style={{ marginTop: 10 }}
                        title={
                          confirmPaymentLink
                            ? 'Guests see a Pay Now button on the Confirmation page.'
                            : 'No payment step shown. Guests receive booking info only. Payment is collected at the hotel.'
                        }
                      />
                      <Divider style={{ margin: '16px 0 12px' }} />
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="payment_timeout_min"
                        label="Payment Timeout"
                        tooltip="Booking will be auto-cancelled if not paid within this time."
                      >
                        <InputNumber
                          min={1}
                          style={{ width: '100%' }}
                          addonAfter="Minutes"
                          placeholder="e.g. 15"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="deposit_payment_code"
                        label="Deposit Payment Code"
                        tooltip="Payment code used for deposit transactions."
                      >
                        <Select
                          allowClear
                          showSearch={{
                            optionFilterProp: ["label"]
                          }}
                          loading={paymentCodeLoading}
                          placeholder="Select a deposit payment code"
                          options={depositPaymentOptions}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: '12px 0' }} />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Space orientation="vertical" size={0}>
                          <Space>
                            <TeamOutlined /> <Text strong>Extra Bed Availability</Text>
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Allow guests to request extra beds.
                          </Text>
                        </Space>
                        <Form.Item
                          name="allow_extrabed"
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Switch />
                        </Form.Item>
                      </div>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: '12px 0' }} />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Space orientation="vertical" size={0}>
                          <Space>
                            <ClockCircleOutlined /> <Text strong>Booking Cut-off Time</Text>
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Block same-day bookings once local time reaches this cut-off.
                          </Text>
                        </Space>
                        <Form.Item name="booking_cutoff_enabled" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch />
                        </Form.Item>
                      </div>
                      <Form.Item
                        name="booking_cutoff_time"
                        label="Cut-off Time"
                        style={{ marginTop: 12, marginBottom: 0 }}
                        dependencies={['booking_cutoff_enabled']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!getFieldValue('booking_cutoff_enabled')) return Promise.resolve();
                              if (!value) return Promise.reject(new Error('Cut-off time is required when enabled'));
                              const timeStr = dayjs.isDayjs(value) ? value.format('HH:mm') : value;
                              if (timeStr === '00:00') {
                                return Promise.reject(new Error('00:00 cannot be used as the cut-off time'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <TimePicker
                          format="HH:mm"
                          minuteStep={5}
                          style={{ width: '100%' }}
                          disabled={!cutoffEnabled}
                          placeholder="e.g. 21:30"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: '12px 0' }} />
                      <Form.Item
                        name="channel_id"
                        label="Channel"
                        tooltip="Channel used for bookings from this property."
                      >
                        <Select
                          allowClear
                          showSearch={{ optionFilterProp: 'label' }}
                          loading={channelLoading}
                          placeholder="Select a channel"
                          options={channelOptions.map((ch: { name: string; id: number }) => ({ label: ch.name, value: ch.id }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card
                  variant="borderless"
                  style={{
                    height: '100%',
                    boxShadow: token.boxShadowTertiary,
                    borderRadius: token.borderRadiusLG,
                  }}
                >
                  <SectionHeader
                    title="Appearance & Theme"
                    icon={<AlertOutlined />}
                    description="Select the visual theme for the booking engine."
                  />
                  <Alert
                    title={
                      <>
                        Current active theme: <Text strong>{currentThemeName}</Text>
                      </>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />
                  <Form.Item
                    name="theme_code"
                    label="Change Theme"
                    tooltip="This will change the look and feel of the customer-facing pages."
                  >
                    <Select
                      placeholder="Select a new theme"
                      options={themeOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                    />
                  </Form.Item>
                </Card>
              </Col>
              <Col xs={24}>
                <Card
                  variant="borderless"
                  style={{
                    boxShadow: token.boxShadowTertiary,
                    borderRadius: token.borderRadiusLG,
                  }}
                >
                  <SectionHeader
                    title="Booking Search Defaults"
                    icon={<UsergroupAddOutlined />}
                    description="Default guest counts pre-filled on the booking search form. Leave empty to use system defaults (2 adults, 0 children, 0 infants)."
                  />
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['default_search_guests', 'adult']}
                        label="Default Adults"
                        tooltip="Minimum 1 adult required."
                      >
                        <InputNumber min={1} max={99} style={{ width: '100%' }} placeholder="2" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['default_search_guests', 'child']}
                        label="Default Children"
                      >
                        <InputNumber min={0} max={99} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['default_search_guests', 'infant']}
                        label="Default Infants"
                      >
                        <InputNumber min={0} max={99} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24}>
                <Card
                  variant="borderless"
                  style={{
                    boxShadow: token.boxShadowTertiary,
                    borderRadius: token.borderRadiusLG,
                  }}
                >
                  <SectionHeader
                    title="Gallery Display"
                    icon={<PictureOutlined />}
                    description="Choose how hotel images appear on the booking page."
                  />
                  <Form.Item name="gallery_mode" style={{ marginBottom: 0 }}>
                    <GalleryModePicker />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 24 }}>
              <Space>
                <Button
                  htmlType="button"
                  icon={<RedoOutlined />}
                  onClick={onReset}
                  disabled={loading}
                >
                  Reset Changes
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  size="large"
                  style={{ minWidth: 120 }}
                  loading={loading}
                >
                  Save Config
                </Button>
              </Space>
            </Row>
          </Form>
        </Spin>
      ) : null}
    </div>
  );
};

export default ConfigPage;
